import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import type { PresignResult } from './presigned-uploader';

/**
 * The asset-upload entities that share the `/storage/admin/pre-signed-url` contract. `entity`
 * decides the storage path, so it is not cosmetic.
 *
 * `replacement_request` and `warranty_claim` additionally **require** `customerId` and `orderId`
 * (BE, story 112366, 2026-07-30); for the three catalog entities those two are optional, which is
 * why widening this union cannot affect device/component/bundle uploads.
 */
export type PresignEntity =
  | 'device'
  | 'component'
  | 'bundle'
  | 'replacement_request'
  | 'warranty_claim';

type PresignResponse = {
  assetId: string;
  uploadUrl: string;
  expiresAt: string;
  requiredHeaders: Record<string, string>;
};

/**
 * Request a presigned upload for a product asset (device/component/bundle image or manual).
 * The backend keys the upload by `assetId` (submitted back in the create/update body) and returns
 * the signed `uploadUrl` plus the `requiredHeaders` the PUT must forward verbatim. `durationSeconds`
 * is sent for videos so the stored asset carries its length. The committed file's `url` is a local
 * object URL for immediate preview — the real displayable URL comes from the detail response on edit.
 */
export async function requestProductPresign(params: {
  file: File;
  entity: PresignEntity;
  attachmentType: 'image' | 'manual';
  durationSeconds?: number;
  /** Required for `replacement_request` / `warranty_claim`, ignored by the catalog entities. */
  customerId?: string;
  orderId?: string;
}): Promise<PresignResult> {
  const { file, entity, attachmentType, durationSeconds, customerId, orderId } = params;
  const res = await apiClient.post<PresignResponse>(endpoints.storage.presignedUrl, {
    fileName: file.name,
    fileType: file.type,
    attachmentType,
    entity,
    // Omitted rather than sent as undefined: the endpoint rejects unknown/!empty properties
    // outright (it is how the old `folder` key was caught), so a catalog upload must not carry
    // these keys at all.
    ...(customerId != null ? { customerId } : {}),
    ...(orderId != null ? { orderId } : {}),
    ...(durationSeconds != null ? { durationSeconds } : {}),
  });
  return {
    uploadUrl: res.uploadUrl,
    putHeaders: res.requiredHeaders,
    file: {
      id: res.assetId,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      contentType: file.type,
    },
  };
}
